// ATTICUS-OPS-FOUNDATION-1 / F1 (2026-06-10) — Claim Setup Readiness UI.
// ReadinessBadge: compact pill driven by a level string (grid rows / headers,
// no fetch). ClaimReadinessPanel: full ✓/⚠ checklist on the Claim Dashboard,
// fetched from GET /claims/{id}/setup-readiness. Read-only; never throws into
// its host (errors render nothing rather than breaking the Overview).
import { useState, useEffect } from "react";
import { apiFetch } from "../api";

const STYLE = {
  ready:           { label: "Ready",           dot: "#16a34a", bg: "#dcfce7", c: "#166534" },
  partially_ready: { label: "Partially Ready", dot: "#d97706", bg: "#fef3c7", c: "#92400e" },
  not_ready:       { label: "Not Ready",       dot: "#dc2626", bg: "#fee2e2", c: "#991b1b" },
};
const FALLBACK = { label: "—", dot: "#94a3b8", bg: "#f1f5f9", c: "#64748b" };

export function ReadinessBadge({ level, compact, sm }) {
  const s = STYLE[level] || FALLBACK;
  if (compact) {
    return <span title={s.label}
      style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: s.dot }} />;
  }
  if (sm) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "1px 6px",
                     borderRadius: 9, background: s.bg, color: s.c, fontSize: 9.5, fontWeight: 700,
                     textTransform: "uppercase", letterSpacing: 0.3, whiteSpace: "nowrap" }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot }} />{s.label}
      </span>
    );
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px",
                   borderRadius: 10, background: s.bg, color: s.c, fontSize: 11, fontWeight: 700,
                   whiteSpace: "nowrap" }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: s.dot }} />{s.label}
    </span>
  );
}

// ReadinessHeaderCheck (CLAIM-OVERVIEW-UX-2): a green ✓ in the claim header once
// setup is complete — replaces the rail's readiness block (which hides itself via
// hideWhenComplete). Renders nothing until fully ready.
export function ReadinessHeaderCheck({ claimId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    apiFetch(`/claims/${claimId}/setup-readiness`).then((d) => { if (live) setData(d); }).catch(() => {});
    return () => { live = false; };
  }, [claimId]);
  const checks = data?.checks || [];
  const ready = checks.length > 0 && checks.every((c) => c.ok);
  if (!ready) return null;
  return (
    <span title="Claim setup complete" style={{ display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0,
      background: "#16a34a", color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 10 }}>
      <span style={{ fontSize: 12, lineHeight: 1 }}>✓</span> Ready
    </span>
  );
}

export function ClaimReadinessPanel({ claimId, hideWhenComplete }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    apiFetch(`/claims/${claimId}/setup-readiness`)
      .then((d) => { if (live) setData(d); })
      .catch(() => { if (live) setErr(true); });
    return () => { live = false; };
  }, [claimId]);

  if (err || !data) return null;
  const { level, checks = [] } = data;
  const done = checks.filter((c) => c.ok).length;
  const ready = checks.length > 0 && done === checks.length;

  // CLAIM-OVERVIEW-UX-2: on the Overview rail, the block disappears once complete
  // (the green ✓ moves to the claim header instead).
  if (ready && hideWhenComplete) return null;

  if (ready && !open) {
    return (
      <div onClick={() => setOpen(true)} title="Setup complete — click to view the checklist"
        style={{ display: "inline-flex", alignItems: "center", gap: 7, marginBottom: 14, padding: "5px 12px",
                 borderRadius: 10, background: "#dcfce7", color: "#166534", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
        <span style={{ fontSize: 15, lineHeight: 1 }}>✓</span> Claim Ready
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", rowGap: 4 }}>
        <h3>Claim Setup Readiness</h3>
        <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minWidth: 0 }}>
          <span style={{ fontSize: 10.5, color: "var(--text2)", whiteSpace: "nowrap" }}>{done}/{checks.length}</span>
          <ReadinessBadge level={level} sm />
          {ready && <button onClick={() => setOpen(false)} className="btn btn-outline btn-sm" style={{ padding: "1px 7px", fontSize: 10 }}>Collapse</button>}
        </span>
      </div>
      <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {checks.map((ch) => (
          <div key={ch.key} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13 }}>
            <span style={{ color: ch.ok ? "#16a34a" : "#d97706", fontWeight: 700, flexShrink: 0, width: 14 }}>
              {ch.ok ? "✓" : "⚠"}
            </span>
            <div>
              <span style={{ color: "var(--text)", fontWeight: ch.ok ? 400 : 600 }}>{ch.label}</span>
              {!ch.ok && ch.detail && (
                <div style={{ color: "var(--text2)", fontSize: 12, marginTop: 1 }}>{ch.detail}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
